import { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileText, Save, Trash2 } from 'lucide-react';

import PageHeader from '../components/PageHeader.jsx';
import SaveBar, { SaveButton } from '../components/SaveBar.jsx';
import FormField, { TextInput } from '../components/FormField.jsx';
import SearchableSelect from '../components/SearchableSelect.jsx';
import { getCustomer, listCustomers } from '../api/customers';
import { listProducts } from '../api/products';
import { createQuote } from '../api/quotes';
import { VAT_RATE } from '../constants/vat';

/**
 * New Quote — same shape as New Sale but submits to /quotes. Sales reps'
 * quotes land in the existing Pending Approval workflow on the desktop ERP.
 *
 * Deliberately doesn't reuse NewSale's component as a single shared form
 * because the long-term divergence is real: quotes have an approval step,
 * an "expires_at", and a different conversion flow downstream.
 */
export default function NewQuote() {
  const navigate = useNavigate();
  const location = useLocation();

  const [customer, setCustomer] = useState(null);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Inline-add flash from the NewCustomer return. We render it in a
  // toast-like banner the same way the list pages do.
  const [flash, setFlash] = useState(location.state?.flash || null);
  // Synchronous double-tap guard — closes the gap between tap and
  // the `submitting` state update where a second tap could fire.
  const submitInFlight = useRef(false);

  // Inline-add return: NewCustomer navigated back here with
  // `newCustomerId` in location.state. Fetch that customer and pre-
  // select them so the rep can carry on building the quote.
  // We clear location.state after handling to avoid re-fetching on
  // subsequent renders (e.g. if React StrictMode double-invokes).
  useEffect(() => {
    const newId = location.state?.newCustomerId;
    if (!newId) return;
    let cancelled = false;
    getCustomer(newId)
      .then((c) => {
        if (cancelled) return;
        setCustomer({
          id: c.id,
          name: c.name,
          sublabel: [c.contact_name, c.city].filter(Boolean).join(' · '),
        });
      })
      .catch(() => {
        // Best-effort — if the fetch fails the rep can re-pick from the
        // list. Avoid surfacing a hard error for what's an edge case.
      });
    // Clear navigation state so a refresh doesn't re-fire this effect.
    navigate(location.pathname, { replace: true, state: null });
    return () => { cancelled = true; };
  }, [location.state, location.pathname, navigate]);

  const loadCustomerOptions = useCallback(async (q) => {
    const data = await listCustomers({ pageSize: 30, search: q || undefined });
    return data.items.map((c) => ({
      id: c.id,
      name: c.name,
      sublabel: [c.contact_name, c.city].filter(Boolean).join(' · '),
    }));
  }, []);

  const loadProductOptions = useCallback(async (q) => {
    const data = await listProducts({ pageSize: 30, search: q || undefined });
    return data.items.map((p) => ({
      id: p.id,
      name: p.name,
      sublabel: [p.sku, p.total_stock != null ? `${p.total_stock} in stock` : null]
        .filter(Boolean)
        .join(' · '),
    }));
  }, []);

  function addLine(product) {
    setLines((curr) => [
      ...curr,
      { tempId: Date.now() + Math.random(), product, quantity: '1', unit_price: '0' },
    ]);
  }

  function updateLine(tempId, patch) {
    setLines((curr) => curr.map((l) => (l.tempId === tempId ? { ...l, ...patch } : l)));
  }

  function removeLine(tempId) {
    setLines((curr) => curr.filter((l) => l.tempId !== tempId));
  }

  const subtotal = lines.reduce(
    (acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0),
    0,
  );
  const vat = subtotal * VAT_RATE;
  const total = subtotal + vat;

  async function onSubmit(e) {
    e.preventDefault();
    if (submitInFlight.current) return;
    if (!customer) {
      setError('Pick a customer first.');
      return;
    }
    if (lines.length === 0) {
      setError('Add at least one line item.');
      return;
    }
    submitInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        customer_id: customer.id,
        line_items: lines.map((l) => ({
          product_id: l.product.id,
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
        })),
        notes: notes || null,
      };
      const quote = await createQuote(payload);
      // Mirror NewSale: land on the quote detail page so user can verify +
      // grab the proforma PDF immediately.
      navigate(`/quotes/${quote.id}`, {
        replace: true,
        state: { flash: `Quote ${quote.quote_number ?? '#' + quote.id} submitted` },
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save quote');
    } finally {
      setSubmitting(false);
      submitInFlight.current = false;
    }
  }

  return (
    <main className="min-h-full px-4 pb-28">
      <PageHeader
        title="New Quote"
        subtitle="Send a quote to a customer"
        icon={FileText}
      />

      {flash && (
        <div
          role="status"
          className="mb-3 mb-card bg-green-50 border-green-200 text-small text-green-900"
        >
          <div className="flex items-center justify-between gap-2">
            <span>{flash}</span>
            <button
              type="button"
              onClick={() => setFlash(null)}
              aria-label="Dismiss"
              className="text-green-700 -mr-1 px-2"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <FormField label="Customer">
          <SearchableSelect
            value={customer}
            onChange={setCustomer}
            placeholder="Pick a customer"
            loadOptions={loadCustomerOptions}
            labelOf={(c) => c.name}
            emptyAction={{
              label: '+ Add new customer',
              onClick: () =>
                navigate('/customers/new', {
                  state: { returnTo: '/quotes/new' },
                }),
            }}
          />
        </FormField>

        <div>
          <p className="text-small font-medium text-gray-700 mb-1.5">Line items</p>
          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.tempId} className="mb-card space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-medium text-gray-900 truncate">
                      {l.product.name}
                    </p>
                    {l.product.sublabel && (
                      <p className="text-caption text-gray-500 truncate">
                        {l.product.sublabel}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLine(l.tempId)}
                    aria-label="Remove line"
                    className="min-h-touch min-w-touch flex items-center justify-center text-danger"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <FormField label="Qty">
                    <TextInput
                      type="number"
                      inputMode="decimal"
                      min="1"
                      step="1"
                      value={l.quantity}
                      onChange={(e) => updateLine(l.tempId, { quantity: e.target.value })}
                    />
                  </FormField>
                  <FormField label="Unit price (ex VAT)">
                    <TextInput
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={l.unit_price}
                      onChange={(e) => updateLine(l.tempId, { unit_price: e.target.value })}
                    />
                  </FormField>
                </div>
                <p className="text-right text-small text-gray-500">
                  Line total: {((Number(l.quantity) || 0) * (Number(l.unit_price) || 0)).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-2">
            <SearchableSelect
              value={null}
              onChange={addLine}
              placeholder="+ Add product"
              loadOptions={loadProductOptions}
              labelOf={(p) => p.name}
            />
          </div>
        </div>

        <FormField label="Notes (optional)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-3 border border-gray-300 rounded-md text-body hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </FormField>

        <section className="mb-card space-y-1">
          <div className="flex justify-between text-small text-gray-600">
            <span>Subtotal (ex VAT)</span>
            <span>{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-small text-gray-600">
            <span>VAT (15.5%)</span>
            <span>{vat.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-title font-semibold text-gray-900 pt-1 border-t border-gray-200">
            <span>Total</span>
            <span>{total.toFixed(2)}</span>
          </div>
        </section>

        {error && <p role="alert" className="text-small text-danger">{error}</p>}

        <p className="text-caption text-gray-500 text-center">
          Sales-rep quotes go to Pending Approval. Manager approves on desktop.
        </p>

        <SaveBar>
          <SaveButton type="submit" icon={Save} loading={submitting} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Quote'}
          </SaveButton>
        </SaveBar>
      </form>
    </main>
  );
}
